import { IonList } from '@ionic/react';
import { JsonFormsCore, JsonSchema, UISchemaElement } from '@jsonforms/core';
import { JsonForms, JsonFormsReactProps } from '@jsonforms/react';
import { vanillaCells, vanillaRenderers } from '@jsonforms/vanilla-renderers';
import Ajv, { ErrorObject } from 'ajv';
import { cloneDeep, debounce, isEqual, merge } from 'lodash';
import {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Form, useBuildFormLazyQuery } from '../../api/graphql/generated';
import { useIsMounted } from '../../util/hooks';
import { logger } from '../../util/logger';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { DeleteButton } from '../button/delete-button';
import { LoadingButton } from '../button/loading-button';
import { LoadingRetry } from '../graphql/LoadingRetry';
import { ionicRenderers } from './ionic-renders';

export type FormData = Record<string, unknown>;
interface BeaconFormProps {
  canDelete?: boolean;
  disabled?: boolean;
  onFormSubmit?: (formData: FormData) => void;
  onFormDelete?: () => void;
  id?: string;
  context: Record<string, unknown>;
  initialData: FormData;
  supplementalButtons?: ReactElement;
  form?: Form;
  customFormElement?: ReactElement;
}

const ajv = new Ajv({ useDefaults: true });

const BeaconForm = (
  props: BeaconFormProps & JsonFormsReactProps
): ReactElement => {
  const {
    onFormSubmit,
    context,
    disabled,
    id,
    initialData,
    canDelete,
    onFormDelete,
    supplementalButtons,
    customFormElement,
  } = props;

  const { i18n } = useTranslation();
  const isMounted = useIsMounted();
  const hasSetDefaultValues = useRef<boolean>(false);
  const [formData, setFormData] = useState<FormData>(initialData);
  const [formErrors, setFormErrors] = useState<ErrorObject[] | undefined>();

  const schema = useRef<JsonSchema>();
  const uischema = useRef<UISchemaElement>();

  const [buildFormQuery, { data: formBuilderData, loading, error }] =
    useBuildFormLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const queryBuildForm = useCallback(
    async (values: FormData) => {
      if (isMounted()) {
        logger.info('calling buildForm', { values, id });
        try {
          await buildFormQuery({
            variables: { input: { context, formId: id, values } },
          });
        } catch (error) {
          logger.error('Error building form', {
            error,
            context,
            formId: id,
            values,
          });
        }
      }
    },
    [id, buildFormQuery, context, isMounted]
  );

  const buildFormDebounced = useMemo(
    () => debounce(queryBuildForm, 500),
    [queryBuildForm]
  );

  const form = props.form ?? formBuilderData?.buildForm.form;
  if (form) {
    schema.current = form.schema;
    uischema.current = form.uischema;
  }

  useEffect(() => {
    buildFormDebounced(formData);
  }, [formData, buildFormDebounced]);
  const stringifiedSchema = JSON.stringify(schema.current);
  useEffect(() => {
    if (schema.current !== undefined && hasSetDefaultValues.current !== true) {
      const validate = ajv.compile(schema.current);
      const clonedInitialData = cloneDeep(initialData);
      validate(clonedInitialData);
      const mergedData = merge({}, clonedInitialData, formData);
      if (!isEqual(mergedData, formData)) {
        setFormData(mergedData);
      }
      hasSetDefaultValues.current = true;
    }
  }, [stringifiedSchema, initialData, formData]);
  const hasErrors = formErrors?.length ? formErrors.length > 0 : false;

  const onChange = (formState: Pick<JsonFormsCore, 'data' | 'errors'>) => {
    const { data, errors } = formState;
    if (isEqual(data, {})) {
      // There is a scenario with json-forms after upgrading to 3.1 or higher
      // the onChange handler is called with an empty object after initial data
      // is seeded. In this case, we skip state.  Not elegant, but a workaround
      // for now.
      return;
    }
    if (!isEqual(data, formData)) {
      setFormData(data);
    }
    if (!isEqual(errors, formErrors)) {
      setFormErrors(errors as unknown as ErrorObject[]);
    }
  };

  const onSubmit = () => {
    if (onFormSubmit && !hasErrors) {
      onFormSubmit(formData);
    }
  };

  return (
    <LoadingRetry
      loading={loading}
      error={error}
      refetch={() => {
        buildFormDebounced(formData);
      }}
      results={{
        hasResults: id !== undefined && schema.current !== undefined,
        noResultsComponent: <div />,
      }}
    >
      <IonList>
        <JsonForms
          data={formData}
          schema={schema.current}
          uischema={uischema.current}
          renderers={[...ionicRenderers, ...vanillaRenderers]}
          cells={vanillaCells}
          onChange={onChange}
        />
        {customFormElement && customFormElement}
      </IonList>
      <br />
      <RightAlignedButtons>
        {supplementalButtons && supplementalButtons}
        {canDelete && <DeleteButton onDelete={onFormDelete} />}
        <LoadingButton
          loading={false}
          buttonProps={{
            type: 'submit',
            disabled: hasErrors || disabled,
            onClick: onSubmit,
          }}
        >
          {i18n.t('common:save')}
        </LoadingButton>
      </RightAlignedButtons>
    </LoadingRetry>
  );
};

export { BeaconForm };
